// Kit de campos compartilhado das Configurações — mantém os tabs curtos e visualmente
// consistentes (labels uppercase, borda 1px slate, foco no brand, data-testid).
import { ShieldCheck, ShieldAlert } from "lucide-react";

export function SettingsCard({ title, icon: Icon, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-6 ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between mb-4">
          {title && (
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              {Icon && <Icon size={18} className="text-brand-600" />} {title}
            </h2>
          )}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function SectionLabel({ children }) {
  return <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-3">{children}</p>;
}

export function Field({ label, value, onChange, type = "text", placeholder, mono = false, required = false, testId, hint, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-slate-500">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition ${mono ? "font-mono" : ""}`}
        {...rest}
      />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function SelectField({ label, value, onChange, options, required = false, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-slate-500">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1 bg-white outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

/**
 * Interruptor. `ariaLabel` é obrigatório na prática: `role="switch"` sem nome
 * é anunciado só como "alternar", sem dizer o quê.
 */
export function Toggle({ checked, onChange, testId, disabled = false, ariaLabel }) {
  return (
    <button
      type="button" role="switch" aria-checked={!!checked} disabled={disabled} aria-label={ariaLabel}
      onClick={() => onChange(!checked)} data-testid={testId}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-50 ${checked ? "bg-brand-600" : "bg-slate-300"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

export function StatusPill({ ok, okText, pendingText }) {
  return ok ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
      <ShieldCheck size={14} /> {okText}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
      <ShieldAlert size={14} /> {pendingText}
    </span>
  );
}
